import React, { useEffect, useState } from 'react';
import { KlarysModeleIA } from './KlarysModeleIA';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, MessageSquareText, Target } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { klarysUrl } from './klarysApi';

const eur = (n) => Number(n || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
const PRODUITS = [['rcds', 'RCDS'], ['domium', 'DOS-DOMIUM'], ['ftpe', 'FTPE']];

const Avancement = ({ data }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="klarys-objectifs-avancement">
    <p className="mb-2 text-xs text-slate-500">{data.jour}ᵉ jour du mois · {data.prorata_pct} % écoulé · alerte si retard &gt; {data.seuil_retard_pct} %</p>
    {!data.items.length ? <p className="text-sm text-slate-500" data-testid="klarys-objectifs-vide">Aucun objectif défini pour ce mois.</p> : data.items.map((o) => (
      <div key={o.cle} className="mb-2.5" data-testid={`klarys-objectif-${o.cle}`}>
        <div className="flex justify-between text-xs"><span className="font-semibold text-slate-800">{o.label}</span>
          <span className={o.retard ? 'text-rose-700 font-semibold' : 'text-emerald-700'}>{o.retard ? 'En retard' : 'Dans le rythme'} · {o.taux} %</span></div>
        <div className="relative mt-1 h-2 rounded-full bg-slate-100">
          <div className={`h-2 rounded-full ${o.retard ? 'bg-rose-500' : 'bg-emerald-500'}`} style={{ width: `${Math.min(o.taux, 100)}%` }} />
          <div className="absolute top-[-3px] h-3.5 w-0.5 bg-slate-500" style={{ left: `${Math.min(data.prorata_pct, 100)}%` }} title="Rythme attendu" />
        </div>
        <p className="mt-0.5 text-[11px] text-slate-500">{eur(o.encaisse)} encaissés sur {eur(o.objectif)} · attendu à date {eur(o.attendu_a_date)}</p>
      </div>
    ))}
  </div>
);

export const KlarysObjectifs = ({ headers, superAdmin }) => {
  const [p, setP] = useState(null);
  const [av, setAv] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => Promise.all([axios.get(klarysUrl('/parametres'), { headers }), axios.get(klarysUrl('/objectifs'), { headers })])
    .then(([a, b]) => { setP(a.data); setAv(b.data); }).catch(() => toast.error('Objectifs indisponibles'));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sauver = async () => {
    setBusy(true);
    try { await axios.put(klarysUrl('/parametres'), p, { headers }); toast.success('Paramètres enregistrés'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Enregistrement impossible'); } finally { setBusy(false); }
  };
  const testSms = async () => {
    try { const r = await axios.post(klarysUrl('/parametres/test-sms'), {}, { headers }); r.data.envoye ? toast.success('SMS de test envoyé') : toast.error(r.data.raison || 'SMS non envoyé'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Test impossible'); }
  };
  if (!p || !av) return <div className="p-6 text-center"><Loader2 className="inline h-5 w-5 animate-spin text-slate-400" /></div>;
  const champ = (val, set, id) => (
    <Input type="number" min="0" step="100" value={val ?? 0} disabled={!superAdmin} onChange={(e) => set(e.target.value)} className="h-8 text-sm" data-testid={id} />
  );
  return (
    <div className="h-full space-y-3 overflow-y-auto bg-[#f6f8fb] p-4" data-testid="klarys-objectifs">
      <KlarysModeleIA p={p} setP={setP} headers={headers} superAdmin={superAdmin} />
      <Avancement data={av} />
      <section className="rounded-xl border border-slate-200 bg-white p-3 space-y-2">
        <h4 className="flex items-center gap-1.5 text-sm font-semibold text-slate-900"><Target className="h-4 w-4 text-[#0a3d62]" /> Objectifs mensuels d'encaissement (CB + SEPA)</h4>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <label className="col-span-2">Global, tous produits {champ(p.objectif_global, (v) => setP({ ...p, objectif_global: v }), 'klarys-obj-global')}</label>
          {PRODUITS.map(([k, l]) => (
            <label key={k}>{l} {champ(p.objectifs_produits[k], (v) => setP({ ...p, objectifs_produits: { ...p.objectifs_produits, [k]: v } }), `klarys-obj-${k}`)}</label>
          ))}
          <label>Seuil d'alerte de retard (%) <Input type="number" min="1" max="90" value={p.seuil_retard_pct} disabled={!superAdmin}
            onChange={(e) => setP({ ...p, seuil_retard_pct: e.target.value })} className="h-8 text-sm" data-testid="klarys-obj-seuil" /></label>
        </div>
      </section>
      <section className="rounded-xl border border-slate-200 bg-white p-3 space-y-2">
        <h4 className="flex items-center gap-1.5 text-sm font-semibold text-slate-900"><MessageSquareText className="h-4 w-4 text-[#0a3d62]" /> Rapport du matin par SMS</h4>
        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!p.controle_recus_rapport} disabled={!superAdmin} onChange={(e) => setP({ ...p, controle_recus_rapport: e.target.checked })} data-testid="klarys-controle-recus-rapport" /> Ajouter au rapport du matin le contrôle des reçus PDF (montants du reçu ≠ souscription)</label>
        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={p.sms_actif} disabled={!superAdmin} onChange={(e) => setP({ ...p, sms_actif: e.target.checked })} data-testid="klarys-sms-actif" /> Envoyer chaque matin une version courte par SMS</label>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <label>Numéro (international) <Input value={p.telephone_sms} disabled={!superAdmin} placeholder="+590690123456" onChange={(e) => setP({ ...p, telephone_sms: e.target.value })} className="h-8 text-sm" data-testid="klarys-sms-tel" /></label>
          <label>Fournisseur
            <select value={p.fournisseur_sms} disabled={!superAdmin} onChange={(e) => setP({ ...p, fournisseur_sms: e.target.value })} data-testid="klarys-sms-fournisseur"
              className="mt-0.5 h-8 w-full rounded-md border border-slate-300 bg-white px-2 text-sm">
              <option value="brevo">Brevo</option><option value="twilio">Twilio</option>
            </select></label>
        </div>
      </section>
      {superAdmin && (
        <div className="flex gap-2">
          <Button size="sm" onClick={sauver} disabled={busy} className="bg-[#0a3d62] hover:bg-[#0c4a77]" data-testid="klarys-param-save">{busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Enregistrer</Button>
          <Button size="sm" variant="outline" onClick={testSms} disabled={!p.sms_actif} data-testid="klarys-sms-test">Envoyer un SMS de test</Button>
        </div>
      )}
    </div>
  );
};
